import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useRouteContext } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Button, EmptyState, ErrorState } from '@bb/ui';
import { ApiError, type SegmentListFilters, type SegmentSummary } from '../../api/client.ts';
import { Can } from '../access/Can.tsx';
import { formatCount } from '../audience/ChannelReachBreakdown.tsx';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
export const formatWhen = (iso: string) => `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;

function Size({ size }: { size: SegmentSummary['size'] }) {
  if (!size) return <span title="Open the segment to estimate its size">—</span>;
  return (
    <span title={`${size.mode === 'exact' ? 'Counted' : 'Estimated'} ${formatWhen(size.computedAt)}`}>
      {formatCount(size.count)}
      {size.mode === 'estimate' ? ' (est.)' : ''}
    </span>
  );
}

/**
 * Audience → Segments (BB-46): saved audiences with their size, tags and last change; search, tag and status filters,
 * «Show starred only» (stars are personal).
 */
export function SegmentsListPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/audience/segments' });
  const { api } = useRouteContext({ from: '/env/$environmentId/audience/segments' });
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);
  const [starred, setStarred] = useState(false);
  const [status, setStatus] = useState<NonNullable<SegmentListFilters['status']>>('active');
  const [tagId, setTagId] = useState('');
  const filters: SegmentListFilters = { status, ...(query ? { query } : {}), ...(starred ? { starred } : {}), ...(tagId ? { tagId } : {}) };
  const key = ['segments', environmentId, filters];
  const list = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => api.listSegments(environmentId, { ...filters, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const tags = useQuery({ queryKey: ['tags', environmentId], queryFn: () => api.listTags(environmentId) });
  const star = useMutation({
    mutationFn: (s: SegmentSummary) => api.starSegment(environmentId, s.id, !s.starred),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['segments', environmentId] }),
  });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const filtered = Boolean(query || starred || tagId || status !== 'active');

  return (
    <section>
      <h1>Segments</h1>
      <p className="hint">Saved audiences for campaigns, Canvas and cards. Sizes are the latest estimate of each definition.</p>
      <Can permission="segments.edit">
        <Link className="button" to="/env/$environmentId/audience/segments/$segmentId" params={{ environmentId, segmentId: 'new' }}>
          Create segment
        </Link>
      </Can>{' '}
      <Link to="/env/$environmentId/audience/segment-insights" params={{ environmentId }}>
        Compare segments
      </Link>
      <div className="filters">
        <input aria-label="Search segments" placeholder="Search by name or description" value={search} onChange={(e) => setSearch(e.target.value)} />{' '}
        <label>
          <input type="checkbox" checked={starred} onChange={(e) => setStarred(e.target.checked)} /> Show starred only
        </label>{' '}
        <select aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="active">Active</option>
          <option value="archived">Archived</option>
          <option value="all">All</option>
        </select>{' '}
        <select aria-label="Filter by tag" value={tagId} onChange={(e) => setTagId(e.target.value)}>
          <option value="">All tags</option>
          {(tags.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>
      {list.isPending ? (
        <p aria-busy="true">Loading segments…</p>
      ) : list.isError ? (
        <ErrorState title="Segments could not be loaded" message={errorText(list.error)} onRetry={() => void list.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title={filtered ? 'No matching segments' : 'No segments yet'} description={filtered ? 'Nothing matches the filters.' : 'Create the first segment to reuse an audience.'} />
      ) : (
        <>
          <table className="table" aria-label="Segments">
            <thead>
              <tr>
                <th scope="col">
                  <span className="bb-visually-hidden">Starred</span>
                </th>
                <th scope="col">Name</th>
                <th scope="col">Size</th>
                <th scope="col">Tags</th>
                <th scope="col">Updated</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id}>
                  <td>
                    <button type="button" className="icon-button" aria-pressed={s.starred} aria-label={`${s.starred ? 'Unstar' : 'Star'} ${s.name}`} onClick={() => star.mutate(s)}>
                      {s.starred ? '★' : '☆'}
                    </button>
                  </td>
                  <td>
                    <Link to="/env/$environmentId/audience/segments/$segmentId" params={{ environmentId, segmentId: s.id }}>
                      {s.name}
                    </Link>
                    {s.status === 'archived' && <span className="tag tag--neutral"> Archived</span>}
                    {s.description && <div className="hint">{s.description}</div>}
                  </td>
                  <td>
                    <Size size={s.size} />
                  </td>
                  <td>
                    {s.tags.map((t) => (
                      <span key={t.id} className="tag">
                        {t.name}
                      </span>
                    ))}
                  </td>
                  <td>
                    {formatWhen(s.updatedAt)}
                    {s.updatedBy?.email ? ` by ${s.updatedBy.email}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.hasNextPage && (
            <Button variant="ghost" onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage}>
              Load more
            </Button>
          )}
        </>
      )}
      {star.isError && <p role="alert">{errorText(star.error)}</p>}
    </section>
  );
}
